import { useEffect } from "react";
import type { SortingState } from "@tanstack/react-table";
import { useSearchParams } from "react-router-dom";
import { useDebounceValue } from "usehooks-ts";

import type { SortDirection } from "~/api/types";
import { SORT_DIRECTIONS } from "~/api/types";

const DEBOUNCE_DURATION_IN_MILLISECONDS = 250;

export const useTableSearchParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  /* PAGINATION */

  const currentPage = parseInt(searchParams.get("page") ?? "") || 1;

  const setCurrentPage = (page: number) => {
    setSearchParams((prev) => {
      prev.set("page", `${page}`);
      return prev;
    });
  };

  /* SORTING */

  const orderBy = searchParams.get("order_by");
  const sortDirection = searchParams.get(
    "sort_direction",
  ) as SortDirection | null;
  const sorting: SortingState = orderBy
    ? [{ desc: sortDirection === SORT_DIRECTIONS.DESC, id: orderBy }]
    : [];

  const setSorting = (sorting: SortingState) => {
    setSearchParams((prev) => {
      if (sorting.length) {
        prev.set("order_by", sorting[0].id);
        prev.set(
          "sort_direction",
          sorting[0].desc ? SORT_DIRECTIONS.DESC : SORT_DIRECTIONS.ASC,
        );
      } else {
        prev.delete("order_by");
        prev.delete("sort_direction");
      }
      return prev;
    });
  };

  /* SEARCHING */

  const search = searchParams.get("search") ?? "";

  const [debouncedSearch, setSearchText] = useDebounceValue<string>(
    search,
    DEBOUNCE_DURATION_IN_MILLISECONDS,
  );

  useEffect(() => {
    if (debouncedSearch === search) {
      return;
    }
    setSearchParams((prev) => {
      if (debouncedSearch === "") {
        prev.delete("search");
      } else {
        prev.set("search", debouncedSearch);
      }
      prev.set("page", "1");
      return prev;
    });
  }, [debouncedSearch, search, setSearchParams]);

  /* END */

  return {
    currentPage,
    setCurrentPage,
    setSorting,
    sorting,
    orderBy,
    sortDirection,
    searchParams,
    setSearchParams,
    search,
    setSearchText,
  };
};
